/* istanbul ignore file */

/**
 * This is a Bullet App convention file.
 * DON'T RENAME OR MOVE TO OTHER FOLDERS.
 *
 * Please only export a default method e.g. called `getRoutes`,
 * which returns an array of `Route` (from react-router-dom v4.x)
 */

import React from 'react';
import {parse as parseQueryString} from 'query-string';
import Loadable from 'react-loadable';
import {Route, Redirect} from 'react-router-dom';

import CookieModal from '@stubhub/react-cookie-modal';
import FeatureToggle from '@stubhub/react-feature-toggle';
import PrivateRoute from '@stubhub/react-private-route';
import Spinner from '@stubhub/react-spinner';
import {connect} from '@stubhub/react-store-provider';

import FEATURE_FLAGS from '../utils/featureFlags';

import controller from './controller';
import {DataLayerConfig} from './dataLayer/dataLayerPageConfig';

const useMockServer = /^(true|1)$/i.test(process.env.REACT_APP_USE_MOCK_SERVER) &&
  process.env.NODE_ENV !== 'production';

const SellHub = Loadable({
  loader: () => import(/* webpackChunkName: "sellhub" */ './sellhub'),
  loading: Spinner
});

const SellSearchResults = Loadable({
  loader: () => import(/* webpackChunkName: "searchresults" */ './searchResults'),
  loading: Spinner
});

const SellTicket = Loadable({
  loader: () => import(/* webpackChunkName: "sellTicket" */ './sellTicket'),
  loading: Spinner
});

const SellEvents = Loadable({
  loader: () => import(/* webpackChunkName: "SellEvents" */ './sellTicket/SellEvents.js'),
  loading: Spinner
});

const ListingSuccess = Loadable({
  loader: () => import(/* webpackChunkName: "listingSuccess" */ './listingSuccess/ListingSuccess'),
  loading: Spinner
});

const ListingDenied = Loadable({
  loader: () => import(/* webpackChunkName: "listingDenied" */ './listingDenied/ListingDenied'),
  loading: Spinner
});

const LoginStateHandler = (props) => (props.loginType === 'CLOSE' ? <Redirect to="/sell" /> : null);

const DismissLogin = connect(controller)(LoginStateHandler);

const SellContentPainter = ({match, location, history, staticContext}) => {
  let {eventIds} = parseQueryString(location.search);
  eventIds = [...new Set(eventIds.split(','))];

  if (eventIds.length > 1) {
    return (
      <SellEvents
        eventIds={eventIds}
        match={match}
        location={location}
        history={history}
        staticContext={staticContext}
      />
    );
  }

  return (
    <SellTicket
      eventIds={eventIds}
      eventId={eventIds[0]}
      match={match}
      location={location}
      history={history}
      staticContext={staticContext}
    />
  );
};

const SellTicketWrapper = ({match, location, history, staticContext}) => {
  return useMockServer ?
    SellContentPainter({match, location, history, staticContext}) :
    <>
      <PrivateRoute
        pageInfo={DataLayerConfig.sellLandingPageLoad}
        key="sellTicket"
        path='/sell/sellTicket'
        render={SellContentPainter}/>
      <DismissLogin/>
    </>;
};

const SellListingSuccessWrapper = ({history}) => (
  <ListingSuccess history={history} />
);

const ListingDeniedWrapper = ({history}) => {
  return (
    <ListingDenied history={history} />
  );
};


export default function getRoutes() {
  const routes = [];
  routes.push(<Route
    exact
    pageInfo={DataLayerConfig.sellLandingPageLoad}
    path="/sell"
    render={(props) => {
      const query = parseQueryString(props.location.search);

      return (
        <>
          <FeatureToggle name={FEATURE_FLAGS.common.cookiesModal} key="cookiesModal">
            <CookieModal />
          </FeatureToggle>
          <SellHub query={query} {...props} />
        </>
      );
    }} key="sellhub"/>);

  /** Search results component */
  routes.push(<Route
    pageInfo={DataLayerConfig.searchResultsPageLoad}
    path='/sell/(searchEvent|performerSearch)'
    render={(props) => {
      const query = parseQueryString(props.location.search);

      return (
        <>
          <FeatureToggle name={FEATURE_FLAGS.common.cookiesModal} key="cookiesModal">
            <CookieModal />
          </FeatureToggle>
          <SellSearchResults query={query} {...props} />
        </>
      );
    }} key="searchResults"/>);

  routes.push(
    <Route
      pageInfo={DataLayerConfig.sellTicketPageLoad}
      key="sellTicketWrapper"
      path="/sell/sellTicket"
      render={({match, location, history, staticContext}) => (
        <SellTicketWrapper match={match} location={location} history={history} staticContext={staticContext} />
      )}
    />
  );

  routes.push(
    <Route
      pageInfo={DataLayerConfig.listingConfirmation}
      key="SellListingSuccessWrapper"
      path="/sell/listingConfirmation"
      render={({history}) => (
        <SellListingSuccessWrapper history={history} />
      )}
    />
  );

  routes.push(
    <Route
      pageInfo={DataLayerConfig.listingDenied}
      key="listingDenied"
      path="/sell/listingDenied"
      render={({history}) => (
        <ListingDeniedWrapper history={history} />
      )}
    />
  );

  return routes;
}
